/**
 * usePragmaticDraggable.ts - React hook for Pragmatic drag and drop
 * 
 * ARCHITECTURE:
 * - Direct wrapper around Pragmatic's draggable() function
 * - Returns cleanup function for React useEffect
 * - Provides TypeScript types for better DX
 */

import { useEffect, useRef, RefObject } from 'react';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

export interface UseDraggableConfig {
  /** Type identifier for this draggable (e.g., 'field', 'dataset', 'card') */
  type: string;
  
  /** Data to attach to the drag operation */
  data?: Record<string, any>;
  
  /** Function to get initial data (called when drag starts) */
  getInitialData?: () => Record<string, any>;
  
  /** Whether this element can be dragged */
  canDrag?: (args: any) => boolean;
  
  /** Callback when drag starts */
  onDragStart?: (args: any) => void;
  
  /** Callback when drag ends */
  onDrop?: (args: any) => void;
  
  /** Callback to generate drag preview */
  onGenerateDragPreview?: (args: any) => void;
}

/**
 * Hook to make an element draggable using Pragmatic drag and drop
 * 
 * @example
 * ```tsx
 * const ref = useRef<HTMLDivElement>(null);
 * 
 * usePragmaticDraggable(ref, {
 *   type: 'card',
 *   data: { id: '123', title: 'My Card' },
 *   onDragStart: () => console.log('Drag started'),
 *   onDrop: () => console.log('Drag ended')
 * });
 * 
 * return <div ref={ref}>Drag me!</div>;
 * ```
 */
export function usePragmaticDraggable<T extends HTMLElement>(
  elementRef: RefObject<T>,
  config: UseDraggableConfig
): void {
  const configRef = useRef(config);
  configRef.current = config;

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const cleanup = draggable({
      element,
      getInitialData: () => ({
        type: configRef.current.type,
        ...(configRef.current.data || {}),
        ...(configRef.current.getInitialData?.() || {})
      }),
      canDrag: configRef.current.canDrag,
      onDragStart: configRef.current.onDragStart,
      onDrop: configRef.current.onDrop,
      onGenerateDragPreview: configRef.current.onGenerateDragPreview,
    });

    return cleanup;
  }, [elementRef]);
}
